<template>
  <div class="p-4">
    <v-card flat rounded="lg" class="trade-dashboard-card">
      <v-card-title class="d-flex align-center flex-wrap ga-2 px-5 py-4">
        <div class="text-xl font-bold">📈 Trade Order Dashboard</div>
        <v-spacer />
        <v-btn color="primary" variant="tonal" rounded="lg" @click="openAddOrderForm">➕ Add Trade Order</v-btn>
        <v-btn color="secondary" variant="tonal" rounded="lg" class="ml-2" @click="openAddStockForm">➕ Add Trade Stock</v-btn>
        <v-btn color="info" variant="tonal" rounded="lg" class="ml-2" @click="fetchOrders">🔄 Refresh</v-btn>
      </v-card-title>

      <v-card-text>
        <TradeOrderTable
          :orders="orders"
          :loading="loading"
          @show-history="showHistory"
        />
      </v-card-text>
    </v-card>

    <!-- Order history -->
    <TradeOrderHistory :order="selectedOrder" v-model="showHistoryDialog" />

    <!-- Trade order form -->
    <TradeOrderForm
      v-model="showOrderForm"
      :editData="selectedOrderForEdit"
      :stockOptions="tradeStockOptions"
      @submit="handleTradeOrderSubmit"
    />

    <!-- Trade stock form -->
    <TradeStockForm
      v-model="showStockForm"
      :editData="selectedStockForEdit"
      :platformOptions="platformOptions"
      :exchangeOptions="exchangeOptions"
      :stock="stock"
      :fetchStocks="fetchPlatformStocks"
      :fetchStockData="fetchStockData"
      @submit="handleTradeStockSubmit"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch } from 'vue'
import { useMutation, provideApolloClient } from '@vue/apollo-composable'
import { apolloClient } from '@/apollo-client'
import { fetchWrapper } from '@/helpers'

// GraphQL imports
import { GET_TRADE_ORDERS } from '@/graphql/tradeOrder/queries'
import { ADD_TRADE_ORDER, UPDATE_TRADE_ORDER } from '@/graphql/tradeOrder/mutations'
import { ADD_TRADE_STOCK, UPDATE_TRADE_STOCK } from '@/graphql/tradeStock/mutations'

// Components
import TradeOrderTable from '@/components/TradeOrder/TradeOrderTable.vue'
import TradeOrderHistory from '@/components/TradeOrder/TradeOrderHistory.vue'
import TradeOrderForm from '@/components/TradeOrder/TradeOrderForm.vue'
import TradeStockForm from '@/components/TradeOrder/TradeStockForm.vue'

const { appContext } = getCurrentInstance()
const optionHelper = appContext.config.globalProperties.$optionHelper
const alertStore$ = appContext.config.globalProperties.$alertStore

// State
const orders = ref([])
const selectedOrder = ref(null)
const selectedOrderForEdit = ref(null)
const selectedStockForEdit = ref(null)
const showHistoryDialog = ref(false)
const showOrderForm = ref(false)
const showStockForm = ref(false)
const loading = ref(false)

const stock = ref([]) // All stocks for TradeStockForm
const tradeStockOptions = ref([]) // Options for TradeOrderForm
const platformOptions = ref([]) // Platforms for TradeStockForm
const exchangeOptions = ref([]) // Exchanges for TradeStockForm

provideApolloClient(apolloClient)

// Order mutations
const { mutate: addOrder } = useMutation(ADD_TRADE_ORDER)
const { mutate: updateOrder } = useMutation(UPDATE_TRADE_ORDER)

// Stock mutations
const { mutate: addStock } = useMutation(ADD_TRADE_STOCK)
const { mutate: updateStock } = useMutation(UPDATE_TRADE_STOCK)

// === Fetch Orders ===
async function fetchOrders() {
  loading.value = true
  try {
    const { data } = await apolloClient.query({
      query: GET_TRADE_ORDERS,
      fetchPolicy: 'no-cache'
    })
    orders.value = data.getTradeOrders || []
  } catch (error) {
    console.error('❌ Failed to load orders:', error.message)
    alertStore$.error("Failed to fetch Trade Orders.")
  } finally {
    loading.value = false
  }
}

// === Show history ===
function showHistory(order) {
  selectedOrder.value = order
  showHistoryDialog.value = true
}

// === Open forms ===
async function openAddOrderForm() {
  await loadTradeStockOptions()
  selectedOrderForEdit.value = null
  showOrderForm.value = true
}

async function openAddStockForm() {
  await loadStockOptions()
  selectedStockForEdit.value = null
  showStockForm.value = true
}

// === Load stock list for TradeStockForm ===
async function loadStockOptions() {
  try {
    const response = await fetchWrapper.post('api/stock')
    stock.value = response.data || []

    // Extract platformOptions and exchangeOptions
    platformOptions.value = await optionHelper.getOptionsFromGraphQL(
      apolloClient,
      'name',
      'platform_id',
      'Platform'
    )

    exchangeOptions.value = await optionHelper.getOptionsFromGraphQL(
      apolloClient,
      'code',
      'exchange_id',
      'Exchange'
    )

  } catch (err) {
    console.error('❌ Failed to load stock options:', err.message)
  }
}

// === Load trade stocks for TradeOrderForm (GraphQL fallback) ===
async function loadTradeStockOptions() {
  if (tradeStockOptions.value.length > 0) return // Already loaded
  try {
    const options = await optionHelper.getOptionsFromGraphQL(
      apolloClient,
      'name',
      'trade_stock_id',
      'TradeStock'
    )
    tradeStockOptions.value = options
  } catch (err) {
    console.error('❌ Failed to load trade stock options:', err.message)
  }
}

// === Platform/Stock fetchers for child ===
async function fetchPlatformStocks(platformId) {
  return stock.value.filter(s => s.platform_id === platformId)
}

async function fetchStockData(stockId) {
  return stock.value.find(s => s.stock_id === stockId) || null
}

// === Handlers ===
async function handleTradeOrderSubmit(data) {
  try {
    loading.value = true
    if (data.trade_order_id) {
      await updateOrder({ trade_order_id: data.trade_order_id, input: { ...data } })
      alertStore$.success("Trade Order updated successfully.")
    } else {
      await addOrder({ input: { ...data } })
      alertStore$.success("New Trade Order added successfully.")
    }
    showOrderForm.value = false
    fetchOrders()
  } catch (error) {
    console.error('❌ Trade order submit failed:', error.message)
    alertStore$.error("Failed to save Trade Order.")
  } finally {
    loading.value = false
  }
}

async function handleTradeStockSubmit(data) {
  try {
    loading.value = true
    if (data.trade_stock_id) {
      await updateStock({ trade_stock_id: data.trade_stock_id, input: { ...data } })
      alertStore$.success("Trade Stock updated successfully.")
    } else {
      await addStock({ input: { ...data } })
      alertStore$.success("New Trade Stock added successfully.")
    }
    showStockForm.value = false
    await loadStockOptions() // Refresh stocks
  } catch (error) {
    console.error('❌ Trade stock submit failed:', error.message)
    alertStore$.error("Failed to save Trade Stock.")
  } finally {
    loading.value = false
  }
}

// Reset selected items when forms close
watch(showOrderForm, (val) => {
  if (!val) selectedOrderForEdit.value = null
})
watch(showStockForm, (val) => {
  if (!val) selectedStockForEdit.value = null
})

// Initial fetch
onMounted(async () => {
  try {
    await fetchOrders()
    await loadStockOptions()
  } catch (err) {
    console.error('❌ Initialization failed', err)
  }
})
</script>

<style scoped>
.trade-dashboard-card {
  border: 1px solid var(--border);
}
</style>